/* Retail product page: gallery, variant picker, quantity, jump links (built from the sections on
   the page), mobile sticky add-to-cart, related products (Shopify recommendations).
   Loaded with `defer`. Safe to load twice. */
(() => {
  if (window.__tolProduct) return;
  window.__tolProduct = true;
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  const behavior = () => (reduce.matches ? 'auto' : 'smooth');
  /* run fn on scroll/resize, once per frame (works for jumps too: anchor links, reload mid-page) */
  const onScroll = (fn) => {
    let queued = false;
    const run = () => { queued = false; fn(); };
    const queue = () => { if (!queued) { queued = true; window.requestAnimationFrame(run); } };
    window.addEventListener('scroll', queue, { passive: true });
    window.addEventListener('resize', queue);
    run();
  };

  /* ---------- gallery ---------- */
  function initGallery(root) {
    const track = root.querySelector('[data-tol-track]');
    const thumbs = [...root.querySelectorAll('[data-tol-thumb]')];
    const prev = root.querySelector('[data-tol-prev]');
    const next = root.querySelector('[data-tol-next]');
    if (!track) return;
    const slides = [...track.children];
    const index = () => Math.round(track.scrollLeft / track.clientWidth);
    const go = (i) => { i = Math.max(0, Math.min(slides.length - 1, i)); track.scrollTo({ left: i * track.clientWidth, behavior: behavior() }); };
    const sync = () => {
      const i = index();
      thumbs.forEach((t, n) => t.setAttribute('aria-current', String(n === i)));
      if (prev) prev.disabled = i === 0;
      if (next) next.disabled = i === slides.length - 1;
      slides.forEach((s, n) => { if (n !== i) s.querySelector('video')?.pause(); });
    };
    thumbs.forEach((t, n) => t.addEventListener('click', () => go(n)));
    prev?.addEventListener('click', () => go(index() - 1));
    next?.addEventListener('click', () => go(index() + 1));
    track.addEventListener('scroll', () => window.requestAnimationFrame(sync), { passive: true });
    root.tolGoToMedia = (mediaId) => { const i = slides.findIndex((s) => s.dataset.mediaId === String(mediaId)); if (i > -1) go(i); };
    sync();
  }

  /* ---------- variant picker + price ---------- */
  function initBuy(root) {
    const dataEl = root.querySelector('[data-tol-variants]');
    const form = root.querySelector('form[action*="/cart/add"]');
    if (!dataEl || !form) return;
    const variants = JSON.parse(dataEl.textContent);
    const idInput = form.querySelector('input[name="id"]');
    const atc = form.querySelector('[data-tol-atc-btn]');
    const priceEl = root.querySelector('[data-tol-price]');
    const compareEl = root.querySelector('[data-tol-compare]');
    const unitEl = root.querySelector('[data-tol-unit]');
    const gallery = root.querySelector('[data-tol-gallery]');
    const labels = { add: atc?.dataset.labelAdd, soldout: atc?.dataset.labelSoldout, unavailable: atc?.dataset.labelUnavailable };
    /* the jump bar and sticky bar buttons submit the same form (form="…"): same disabled state as the main button */
    const formId = form.getAttribute('id'); // not form.id: the <input name="id"> shadows that property
    const others = formId ? [...document.querySelectorAll(`button[form="${formId}"]`)] : [];
    const setAvailable = (ok) => { atc.disabled = !ok; others.forEach((b) => { b.disabled = !ok; }); };

    root.querySelectorAll('[data-tol-option]').forEach((input) => input.addEventListener('change', () => {
      const chosen = [...root.querySelectorAll('[data-tol-option]:checked')].map((i) => i.value);
      const v = variants.find((x) => x.options.every((o, n) => o === chosen[n]));
      if (!v) { setAvailable(false); atc.querySelector('span').textContent = labels.unavailable; return; }
      idInput.value = v.id;
      setAvailable(v.available);
      atc.querySelector('span').textContent = v.available ? labels.add : labels.soldout;
      if (priceEl) priceEl.textContent = v.price_formatted;
      if (compareEl) { compareEl.textContent = v.compare_formatted || ''; compareEl.hidden = !v.compare_formatted; }
      if (unitEl) { unitEl.textContent = v.unit_formatted || ''; unitEl.hidden = !v.unit_formatted; }
      if (v.media_id && gallery?.tolGoToMedia) gallery.tolGoToMedia(v.media_id);
      document.querySelectorAll('[data-tol-variant-mirror]').forEach((el) => { el.textContent = v.mirror; });
      const url = new URL(window.location.href); url.searchParams.set('variant', v.id); window.history.replaceState({}, '', url);
    }));

    root.querySelectorAll('[data-tol-qty]').forEach((box) => {
      const input = box.querySelector('input');
      box.addEventListener('click', (e) => {
        const b = e.target.closest('button'); if (!b) return;
        const val = Math.max(1, (parseInt(input.value, 10) || 1) + (b.dataset.step === 'up' ? 1 : -1));
        input.value = val;
      });
    });

    /* mobile sticky bar: visible once the main button has scrolled above the screen.
       Hidden = inert (not focusable, not read). The bar is display:none on desktop. */
    const sticky = document.querySelector('[data-tol-stickybuy]');
    if (sticky && atc) {
      onScroll(() => {
        const show = atc.getBoundingClientRect().bottom < 0;
        sticky.classList.toggle('is-visible', show);
        sticky.inert = !show;
      });
    }
  }

  /* ---------- jump links: one per [data-tol-jump] section, in page order ---------- */
  function initJump(nav) {
    const list = nav.querySelector('[data-tol-jump-links]');
    const targets = [...document.querySelectorAll('[data-tol-jump]')].filter((t) => t.id && t.dataset.tolJump);
    if (!list || !targets.length) { nav.hidden = true; return; }
    list.replaceChildren(...targets.map((t) => {
      const li = document.createElement('li'); const a = document.createElement('a');
      a.href = `#${t.id}`; a.textContent = t.dataset.tolJump; li.append(a); return li;
    }));
    const links = [...list.querySelectorAll('a')];
    /* fixed under the sticky header once its normal place scrolls past (offset = CSS --tol-jump-top);
       the placeholder keeps the page from jumping when the bar leaves the flow */
    const placeholder = document.createElement('div');
    nav.before(placeholder);
    onScroll(() => {
      const offset = parseFloat(getComputedStyle(nav).getPropertyValue('--tol-jump-top')) || 0;
      const fixed = placeholder.getBoundingClientRect().top < offset;
      if (fixed !== nav.classList.contains('is-fixed')) {
        placeholder.style.height = fixed ? `${nav.offsetHeight}px` : '';
        nav.classList.toggle('is-fixed', fixed);
      }
    });
    if ('IntersectionObserver' in window) {
      /* highlight the section in view; keep the active link visible in the scrolling row */
      const spy = new IntersectionObserver((entries) => entries.forEach((e) => {
        if (!e.isIntersecting) return;
        links.forEach((a) => a.setAttribute('aria-current', String(a.getAttribute('href') === `#${e.target.id}`)));
        const active = links.find((a) => a.getAttribute('aria-current') === 'true');
        if (active) list.scrollTo({ left: active.offsetLeft - 16, behavior: behavior() });
      }), { rootMargin: '-40% 0px -55% 0px' });
      targets.forEach((t) => spy.observe(t));
    }
  }

  /* ---------- related products (Section Rendering API) ----------
     The server renders fallback cards (Retail packs collection). If Shopify returns recommendations,
     they replace the fallback; otherwise the fallback stays. No cards at all = section stays hidden. */
  async function initRelated(el) {
    if (el.dataset.loaded) return;
    el.dataset.loaded = '1';
    const section = el.closest('[data-tol-related-section]');
    try {
      const res = await fetch(el.dataset.url);
      if (!res.ok) return;
      const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
      const fresh = doc.querySelector('[data-tol-related]');
      if (fresh && fresh.querySelector('.tol-pcard')) {
        el.innerHTML = fresh.innerHTML;
        if (section) section.hidden = false;
      }
    } catch (e) { /* keep the fallback cards */ }
  }

  /* ---------- round colour clip (About): play muted while on screen; "reduce motion" = poster only ---------- */
  function initClip(box) {
    const v = box.querySelector('video');
    if (!v || v.dataset.tolReady) return;
    v.dataset.tolReady = '1';
    v.muted = true;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || !('IntersectionObserver' in window)) return;
    new IntersectionObserver((entries) => entries.forEach((e) => (e.isIntersecting ? v.play().catch(() => {}) : v.pause())), { threshold: 0.4 }).observe(v);
  }

  function init(scope = document) {
    scope.querySelectorAll('[data-tol-gallery]').forEach(initGallery);
    scope.querySelectorAll('[data-tol-buy]').forEach(initBuy);
    scope.querySelectorAll('[data-tol-jumpnav]').forEach(initJump);
    scope.querySelectorAll('[data-tol-related][data-url]').forEach(initRelated);
    scope.querySelectorAll('[data-tol-clip]').forEach(initClip);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
  document.addEventListener('shopify:section:load', (e) => init(e.target));
})();
